import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import api from '../lib/api';
import { assetUrl } from '../components/GuideContent';
import { logError } from '../lib/log';
import { usePageMeta } from '../hooks/usePageMeta';
import { useReading } from '../hooks/useReading';

const SITE_URL = (process.env.REACT_APP_SITE_URL || '').replace(/\/$/, '');

export default function AreaPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound | error
  const { isAuthed, readSlugs } = useReading();
  const area = data?.area;

  usePageMeta({
    title: area?.name ? `${area.name} | OVERALL WELLBEING` : 'Area of Life | OVERALL WELLBEING',
    description: area?.description || 'Every part of daily life, explored step by step, with practical guidance for each area.',
    path: `/areas/${slug}`,
    image: area?.share_image ? assetUrl(area.share_image) : undefined,
    noindex: status === 'notfound' || area?.seo_index === false,
  });

  useEffect(() => {
    let active = true;
    setStatus('loading');
    (async () => {
      try {
        const { data: payload } = await api.get(`/knowledge/areas/${slug}`);
        if (!active) return;
        setData(payload);
        setStatus('ok');
      } catch (e) {
        if (!active) return;
        logError('AreaPage.load', e);
        setStatus(e.response?.status === 404 ? 'notfound' : 'error');
      }
    })();
    return () => { active = false; };
  }, [slug]);

  if (status === 'loading') {
    return (
      <div className="w-full flex items-center justify-center py-space-3xl" data-testid="area-loading">
        <span className="inline-block w-2 h-2 rounded-full bg-surface-tint animate-pulse" />
        <span className="ml-space-sm font-label-md text-label-md uppercase tracking-wider text-on-surface-variant">Opening this area of life…</span>
      </div>
    );
  }

  if (status === 'notfound' || status === 'error') {
    return (
      <div className="w-full px-margin-mobile lg:px-margin py-space-3xl" data-testid="area-notfound">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="font-headline-md text-headline-md uppercase text-outline tracking-tight mb-space-sm">Area of life not found</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mb-space-lg">Something went wrong. Please try again, or browse all the Areas of Life.</p>
          <Link to="/wellbeing" className="inline-flex items-center gap-space-xs h-12 px-space-xl rounded-lg bg-surface-tint text-on-primary font-label-md text-label-md uppercase tracking-wider hover:bg-primary transition-all">
            <Icon name="arrow_back" className="text-[18px]" /> All Areas of Life
          </Link>
        </div>
      </div>
    );
  }

  const guides = data.guides || [];
  const intro = area.intro_sections || [];
  const header = area.header_image;

  const ld = SITE_URL ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Areas of Life', item: `${SITE_URL}/wellbeing` },
      { '@type': 'ListItem', position: 3, name: area.name, item: `${SITE_URL}/areas/${area.slug}` },
    ],
  } : null;

  return (
    <div className="flex flex-col w-full" data-testid="area-page">
      {ld ? <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} /> : null}

      {/* Breadcrumbs */}
      <section className="w-full px-margin-mobile lg:px-margin pt-space-xl pb-space-lg">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-space-md">
          <div className="flex items-center flex-wrap gap-space-xs text-on-surface-variant font-label-md text-label-md uppercase tracking-wider">
            <Link to="/wellbeing" className="hover:text-surface-tint transition-colors">AREAS OF LIFE</Link>
            <span className="text-surface-container-highest">/</span>
            <span className="text-outline font-extrabold" data-testid="area-name">{area.name}</span>
          </div>
          <Link to="/wellbeing" className="inline-flex items-center gap-space-xs font-label-md text-label-md uppercase tracking-wider text-surface-tint font-bold hover:text-primary transition-colors">
            <Icon name="arrow_back" className="text-[18px]" /> All Areas of Life
          </Link>
        </div>
      </section>

      {/* HERO */}
      <section className="w-full px-margin-mobile lg:px-margin pb-space-2xl bg-surface relative overflow-hidden">
        <div className="absolute top-0 right-1/4 w-[440px] h-[440px] rounded-full bg-primary-container/50 blur-[120px] pointer-events-none -z-10" />
        <div className="max-w-7xl mx-auto">
          {header?.file ? (
            <img
              src={assetUrl(header.file)}
              alt={header.alt || ''}
              width={header.width || undefined}
              height={header.height || undefined}
              loading="eager"
              decoding="sync"
              className="w-full h-auto rounded-xl shadow-sm mb-space-xl"
              data-testid="area-header-image"
            />
          ) : null}
          <div className="flex flex-wrap items-center gap-space-sm mb-space-md">
            <span className="w-2 h-2 rounded-full bg-surface-tint shadow-[0_0_8px_rgba(240,40,223,0.7)]" />
            <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold">AREA OF LIFE {String(area.order || '').padStart(2, '0')}</span>
          </div>
          <h1 data-testid="area-title" className="font-headline-lg text-headline-lg lg:text-display-hero lg:font-display-hero uppercase text-outline tracking-tight leading-tight lg:leading-none mb-space-lg">
            {area.name}
          </h1>
          {area.description ? (
            <p className="font-body-lg text-body-lg text-on-surface-variant max-w-3xl leading-relaxed" data-testid="area-description">{area.description}</p>
          ) : null}
        </div>
      </section>

      {/* INTRO */}
      {intro.length ? (
        <section className="w-full px-margin-mobile lg:px-margin py-space-2xl bg-surface-container-low" data-testid="area-intro">
          <div className="max-w-4xl mx-auto flex flex-col gap-space-lg">
            {intro.map((s) => (
              <div key={s.heading} className="bg-surface p-space-xl rounded-lg shadow-sm" data-testid={`area-intro-${s.heading.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <h2 className="font-headline-sm text-headline-sm uppercase text-outline mb-space-md">{s.heading}</h2>
                <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* START HERE */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-2xl bg-surface">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-space-xl pb-space-md border-b border-surface-container">
            <div>
              <span className="font-label-md text-label-md text-surface-tint uppercase tracking-widest font-bold block mb-space-xs">CHOOSE A GUIDE</span>
              <h2 className="font-headline-md text-headline-md text-outline uppercase tracking-tight">Start here</h2>
            </div>
            {guides.length ? (
              <span className="font-label-md text-label-md uppercase tracking-wider text-on-surface-variant mt-space-sm md:mt-0" data-testid="area-reading-count">
                {isAuthed
                  ? `You've read ${guides.filter((t) => readSlugs.includes(t.slug)).length} of ${guides.length} guides in this area`
                  : `${guides.length} guide${guides.length === 1 ? '' : 's'}`}
              </span>
            ) : null}
          </div>

          {guides.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-space-md" data-testid="area-guides">
              {guides.map((t) => (
                <Link
                  key={t.slug}
                  to={`/topics/${t.slug}`}
                  data-testid={`area-guide-${t.slug}`}
                  className="p-space-lg bg-surface-container-low rounded-lg hover:bg-primary-container transition-colors group flex items-start justify-between shadow-sm relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 w-1 h-full bg-surface-tint opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="pr-space-sm">
                    <h3 className="font-title-md text-title-md text-outline font-bold group-hover:text-surface-tint transition-colors">{t.display_title || t.title}</h3>
                    <p className="font-body-sm text-body-sm text-on-surface-variant mt-1">{t.domain}</p>
                    {readSlugs.includes(t.slug) ? (
                      <span className="font-label-sm text-label-sm uppercase tracking-wider text-surface-tint font-bold mt-1 block" data-testid={`area-read-flag-${t.slug}`}>✓ Read</span>
                    ) : null}
                  </div>
                  <Icon name="chevron_right" className="text-outline group-hover:text-surface-tint group-hover:translate-x-1 transition-all shrink-0" />
                </Link>
              ))}
            </div>
          ) : (
            <p className="font-body-md text-body-md text-on-surface-variant" data-testid="area-guides-empty">
              {area.start_here_empty_text || 'Guides coming soon'}
            </p>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="w-full px-margin-mobile lg:px-margin py-space-3xl bg-surface-container">
        <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
          <span className="font-label-md text-label-md text-surface-tint uppercase tracking-widest font-bold mb-space-xs">KEEP EXPLORING</span>
          <h2 className="font-headline-lg text-headline-lg text-outline uppercase tracking-tight mb-space-md">One calm step at a time</h2>
          <p className="font-body-lg text-body-lg text-on-surface-variant max-w-xl mb-space-2xl">Explore another area of life, or browse the full library of practical guides.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-space-md w-full sm:w-auto">
            <Link to="/wellbeing" className="w-full sm:w-auto px-space-xl h-12 rounded bg-surface-tint text-on-primary font-label-md text-label-md uppercase tracking-widest font-bold shadow-[0_8px_24px_rgba(240,40,223,0.30)] hover:bg-primary transition-all flex items-center justify-center gap-space-xs">
              <Icon name="grid_view" className="text-[20px]" /> All Areas of Life
            </Link>
            <Link to="/resources" className="w-full sm:w-auto px-space-xl h-12 rounded bg-outline text-surface font-label-md text-label-md uppercase tracking-widest font-bold hover:bg-on-surface transition-all flex items-center justify-center gap-space-xs">
              <Icon name="menu_book" className="text-[20px]" /> Browse the Library
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
